import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Sidebar from "../../sidebar/sidebar";
import { BaseUrl } from "../../../Environment";
import { getSecureItem } from "../../../utilsFunctions";
import { getAllBankAccountsDataAPI } from "../../../service/service";
import { generateSavingsSchedule } from "./savingsCalculator";
import { mergeSchemes, normalize } from "../Schemes/staticSchemes";

const PAYMENT_MODES = ["Cash", "Online", "Cheque"];

const TOAST_OPTS = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: false,
};

const INITIAL_FORM = {
  savingMethod: "recurring",
  schemeId: "",
  customerId: "",
  customerLabel: "",
  totalAmount: "",
  totalInterest: "",
  rateOfInterest: "",
  dailyCollAmt: "",
  tenureType: "daily",
  interestCompounding: "",
  tenure: "",
  startDate: new Date().toISOString().split("T")[0],
  nextDueDate: new Date().toISOString().split("T")[0],
  processFee: "",
  accountId: "",
  collectorId: "",
  paymentType: "Cash",
};

const InterestCompounding = [
  "Monthly",
  "Quarterly",
  "Half Yearly",
  "Yearly",
  "No Compounding",
];

const getSchemeKey = (scheme) =>
  scheme.schemeId ?? `static:${scheme.schemeType}`;

const useStyles = makeStyles((theme) => ({
  pageHeading: { textAlign: "center", marginTop: 15 },
  headingText: {
    fontWeight: 600,
    fontSize: "20px",
    color: "#3E4664",
    [theme.breakpoints.down("xs")]: {
      fontSize: "18px",
    },
  },
  wrapper: {
    marginLeft: "73px",
    // marginTop: "3rem",
    padding: theme.spacing(0.5, 3),
    [theme.breakpoints.down("xs")]: {
      marginTop: "1.5rem",
      padding: theme.spacing(0, 1),
    },
  },
  card: {
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  label: {
    fontWeight: 400,
    marginBottom: theme.spacing(1),
    color: "#8E8E98",
    fontSize: 14,
  },
  required: { color: "#e53935", marginLeft: 2 },
  textField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 35,
      borderRadius: 5,

      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #d4d4d5",
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d4d4d5",
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d4d4d5",
        borderWidth: 1,
      },
    },
  },
  tenureType: {
    width: 100,
    "& .MuiInputBase-root": {
      background: "#EFF2FF",
      borderRadius: 6,
      height: 30,
    },

    "& .MuiSelect-select": {
      padding: "10px 28px 10px 8px",
    },
  },
  savingMethod: {
    width: 260,
    "& .MuiInputBase-root": {
      borderRadius: 5,
      height: 35,
      border: "1px solid lightgrey",
    },
  },
  calcButtonsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  calculateButton: {
    backgroundColor: "#4D66F0",
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(0.5, 3),
    fontSize: 15,
    "&:hover": { backgroundColor: "#5a52e0" },
  },
  resetButton: {
    textTransform: "none",
    fontSize: 15,
    padding: theme.spacing(0.5, 3),
  },
  totalCircle: {
    width: 190,
    height: 190,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #13c9a3, #3f6fea)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "1.8rem",
    fontWeight: 700,
    margin: "0 auto",
  },
  totalWrapper: { textAlign: "center", marginTop: theme.spacing(4) },
  totalLabel: { fontWeight: 600, marginTop: theme.spacing(2) },
  scheduleSection: { margin: theme.spacing(4) },
  scheduleTitle: { fontWeight: 600, marginBottom: theme.spacing(2) },
  tableHeadRow: { backgroundColor: "#eef2fb" },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(4),
    [theme.breakpoints.down("xs")]: { justifyContent: "center" },
  },
  cancelButton: {
    color: "#5a5a5a",
    border: "1px solid #D4D4D5",
    textTransform: "none",
    fontSize: 15,
    padding: theme.spacing(0.5, 3),
  },
  saveButton: {
    backgroundColor: "#13c902",
    color: "#fff",
    textTransform: "none",
    fontSize: 15,
    padding: theme.spacing(0.5, 3),
    "&:hover": { backgroundColor: "#10a802" },
    "&:disabled": { backgroundColor: "#a5e6a0", color: "#fff" },
  },
  assignRow: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(0, 4),
  },
  rowCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AddSaving = () => {
  const classes = useStyles();
  const history = useHistory();
  const isMounted = useRef(true);

  const [form, setForm] = useState(INITIAL_FORM);
  const [schemes, setSchemes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [schedule, setSchedule] = useState([]);
  const [maturityValue, setMaturityValue] = useState(0);

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [schemeSaving, setSchemeSaving] = useState(false);

  const isFixedDeposit = form.savingMethod === "fixed";

  const authHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: "Bearer " + getSecureItem("token"),
    }),
    [],
  );

  const handleChange = (field) => (event) => {
    event.persist();
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    // any field change invalidates the previously calculated schedule
    // setSchedule([]);
  };

  const handleSavingMethodChange = (event) => {
    const savingMethod = event.target.value;
    setForm((prev) => ({
      ...prev,
      savingMethod,
      // Fixed Deposit doesn't compound, so clear any compounding
      // selection made while Recurring was active.
      interestCompounding:
        savingMethod === "fixed" ? "No Compounding" : prev.interestCompounding,
    }));
    setSchedule([]);
  };

  const fetchSchemes = useCallback(async () => {
    const loggedinUser = getSecureItem("loggedinUser");
    const resp = await axios.get(
      `${BaseUrl}/scheme/getAllSchemes?companyId=${loggedinUser?.companyId}`,
      { headers: authHeaders() },
    );
    return mergeSchemes(resp.data?.data || []);
  }, [authHeaders]);

  const applyScheme = (scheme, schemeId) => {
    setForm((prev) => ({
      ...prev,
      schemeId,
      // auto-fill from the scheme, still editable afterwards
      rateOfInterest: scheme?.interestRate ?? prev.rateOfInterest,
      interestCompounding: scheme?.compType ?? "",
      tenure: scheme?.tenure ?? prev.tenure,
    }));
    setSchedule([]);
  };

  const handleSchemeChange = async (event) => {
    const key = event.target.value;
    const selected = schemes.find(
      (s) => String(getSchemeKey(s)) === String(key),
    );
    if (!selected) return;

    if (selected.schemeId) {
      applyScheme(selected, selected.schemeId);
      return;
    }

    const loggedinUser = getSecureItem("loggedinUser");
    const { schemeType, defaults } = selected;

    try {
      setSchemeSaving(true);

      const resp = await axios.post(
        `${BaseUrl}/scheme/addScheme`,
        {
          schemeType,
          ...defaults, // interestRate, tenure, compType
          userId: loggedinUser?.userId,
          companyId: loggedinUser?.companyId,
        },
        { headers: authHeaders() },
      );

      if (!resp.data?.success) {
        toast.error("Unable to add savings type. Please try again!", TOAST_OPTS);
        return;
      }

      const list = await fetchSchemes();
      if (!isMounted.current) return;
      setSchemes(list);

      const newId =
        resp.data?.data?.schemeId ??
        resp.data?.result?.schemeId ??
        resp.data?.schemeId;

      const created =
        list.find((s) => s.schemeId && newId && String(s.schemeId) === String(newId)) ||
        list.find(
          (s) => s.schemeId && normalize(s.schemeType) === normalize(schemeType),
        );

      if (!created) {
        toast.error("Savings type was added but could not be loaded!", TOAST_OPTS);
        return;
      }

      applyScheme(created, created.schemeId);
    } catch (err) {
      toast.error("Unable to add savings type. Please try again!", TOAST_OPTS);
    } finally {
      if (isMounted.current) setSchemeSaving(false);
    }
  };

  const backLinkAction = () => history.push("/savings");

  // ---- initial data: schemes / accounts / collectors / customers ----
  useEffect(() => {
    isMounted.current = true;
    const loggedinUser = getSecureItem("loggedinUser");

    const fetchLookups = async () => {
      setLoading(true);
      try {
        const bankPayload = {
          companyId: loggedinUser?.companyId,
          searchQuery: "",
        };
        const [schemeResp, accountResp, collectorResp, customerResp] =
          await Promise.all([
            axios.get(
              `${BaseUrl}/scheme/getAllSchemes?companyId=${loggedinUser?.companyId}`,
              {
                headers: authHeaders(),
              },
            ),
            await getAllBankAccountsDataAPI(bankPayload),
            axios.get(
              `${BaseUrl}/user/getAllUsers?userName=&companyId=${loggedinUser?.companyId}&branchId=`,
              {
                headers: authHeaders(),
              },
            ),
            await axios.get(
              BaseUrl +
                `/customer/getAllCustomers?name=&companyId=${loggedinUser?.companyId}`,
              {
                headers: authHeaders(),
              },
            ),
          ]);

        if (!isMounted.current) return;
        setSchemes(mergeSchemes(schemeResp.data?.data || []));
        if(accountResp?.data?.length > 0){
          setAccounts(accountResp?.data);
          setForm((prev) => ({
          ...prev,
          accountId: accountResp?.data?.[accountResp?.data?.length-1]?.accountId,
        }))
        } else {
          setAccounts([]);
        }

        setCollectors(collectorResp.data?.result || []);
        setCustomers(customerResp.data || []);
      } catch (err) {
        // Even if the API fails, still offer the static savings types
        if (isMounted.current) setSchemes(mergeSchemes([]));
        toast.error("Unable to load savings form data!", TOAST_OPTS);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchLookups();

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Calculate: build the savingsCollection schedule client-side ----
  const handleCalculate = () => {
    setSubmitted(true);

    const {
      customerId,
      tenure,
      tenureType,
      nextDueDate,
      dailyCollAmt,
      rateOfInterest,
    } = form;

    if (
      !customerId ||
      !tenure ||
      !tenureType ||
      !nextDueDate ||
      !dailyCollAmt
    ) {
      toast.error(
        "Please fill Customer, Tenure, Tenure Type, Due Start Date and Collection Amount!",
        TOAST_OPTS,
      );
      return;
    }

    try {
      const loggedinUser = getSecureItem("loggedinUser");
      const payload = {
        savingMethod: form.savingMethod,
        tenure: +tenure,
        startDate: nextDueDate,
        dailyCollAmt: +dailyCollAmt,
        rateOfInterest: +(rateOfInterest || 0),
        customerId: +customerId,
        collectorId: form.collectorId ? +form.collectorId : null,
        accountId: form.accountId ? +form.accountId : null,
        tenureType,
        interestCompounding: form.interestCompounding,
        companyId: loggedinUser.companyId,
        paymentType: form.paymentType,
      };

      const { schedule, summary } = generateSavingsSchedule(payload);

      setSchedule(schedule);
      setForm((prev) => ({
        ...prev,
        totalAmount: summary.principalAmount,
        totalInterest: summary.interestEarned,
      }));
      setMaturityValue(Number(summary.maturityAmount?.toFixed(2)));
    } catch (err) {
      toast.error(err.message || "Unable to calculate schedule", TOAST_OPTS);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setSchedule([]);
    setMaturityValue(0);
    setSubmitted(false);
  };

  // ---- Save: POST { saving, savingsCollections } to the addSaving API ----
  const handleSave = async () => {
    if (schedule.length === 0) {
      toast.error("Please calculate the schedule before saving!", TOAST_OPTS);
      return;
    }

    const loggedinUser = getSecureItem("loggedinUser");

    const savingPayload = {
      schemeId: form.schemeId,
      totalAmount: Number(form.totalAmount) || 0,
      customerId: Number(form.customerId),
      rateOfInterest: Number(form.rateOfInterest || 0),
      dailyCollAmt: Number(form.dailyCollAmt),
      tenureType: form.tenureType,
      savingMethod: form.savingMethod,
      totalInterest: Number(form.totalInterest),
      tenure: Number(form.tenure),
      startDate: form.startDate,
      nextDueDate: form.nextDueDate,
      processFee: Number(form.processFee) || 0,
      accountId: form.accountId || null,
      collectorId: form.collectorId || null,
      savingStatus: "Open",
      paymentStatus: "Due",
      companyId: loggedinUser?.companyId,
      userId: loggedinUser?.userId,
    };

    try {
      setSaving(true);
      const resp = await axios.post(
        `${BaseUrl}/savings/addSaving`,
        { saving: savingPayload, savingsCollections: schedule },
        { headers: authHeaders() },
      );

      if (resp.data?.success) {
        toast.success("Savings Added Successfully!", TOAST_OPTS);
        history.push("/savings");
      } else {
        toast.error("Please try again later!", TOAST_OPTS);
      }
    } catch (err) {
      toast.error("Please try again later!", TOAST_OPTS);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  return (
    <div>
      <Sidebar />

      <div className={classes.pageHeading}>
        <Typography className={`Fonts ${classes.headingText}`}>
          Add Savings
        </Typography>
      </div>

      <div className={classes.wrapper}>
        <div
          style={{
            display: "flex",
            marginRight: 40,
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <TextField
            select
            variant="standard"
            value={form.savingMethod}
            onChange={handleSavingMethodChange}
            className={classes.savingMethod}
          >
            <MenuItem classes={"Fonts"} value="recurring" className="Fonts">
              Recurring Deposit
            </MenuItem>
            <MenuItem classes={"Fonts"} value="fixed" className="Fonts">
              Fixed Deposit (Simple Interest)
            </MenuItem>
          </TextField>
        </div>
        <Card className={classes.card}>
          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 40 }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Grid container spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Savings Types<span className={classes.required}>*</span>
                    </Typography>
                    <TextField
                      select
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      value={form.schemeId}
                      error={submitted && !form.schemeId}
                      disabled={schemeSaving}
                      helperText={schemeSaving ? "Adding savings type..." : ""}
                      onChange={handleSchemeChange}
                    >
                      <MenuItem disabled value="">
                        <em>Select Savings Type</em>
                      </MenuItem>
                      {schemes.map((s) => (
                        <MenuItem
                          className="Fonts"
                          key={getSchemeKey(s)}
                          value={getSchemeKey(s)}
                        >
                          {s.schemeType}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Application Date
                      <span className={classes.required}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      value={form.startDate}
                      error={submitted && !form.startDate}
                      onChange={handleChange("startDate")}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Due Start Date<span className={classes.required}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      value={form.nextDueDate}
                      error={submitted && !form.nextDueDate}
                      onChange={handleChange("nextDueDate")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Choose Customer<span className={classes.required}>*</span>
                    </Typography>
                    <TextField
                      select
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      placeholder="Search customer by name or ID"
                      value={form.customerId}
                      error={submitted && !form.customerId}
                      helperText={
                        submitted && !form.customerId
                          ? "Choose a valid customer"
                          : ""
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          customerId: e.target.value,
                        }))
                      }
                    >
                      <MenuItem disabled value="">
                        <em>Select Customer</em>
                      </MenuItem>
                      {customers?.map((c) => (
                        <MenuItem key={c?.customerId} value={c?.customerId}>
                          {c?.firstName + " " + c?.lastName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Interest Compounding only applies to Recurring Deposit */}
                  {!isFixedDeposit ? (
                    <Grid item xs={12}>
                      <Typography className={`Fonts ${classes.label}`}>
                        Interest Compounding
                      </Typography>
                      <TextField
                        select
                        className={`Fonts ${classes.textField}`}
                        variant="outlined"
                        value={form.interestCompounding}
                        onChange={handleChange("interestCompounding")}
                      >
                        {InterestCompounding?.map((mode) => (
                          <MenuItem key={mode} value={mode}>
                            {mode}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography className={`Fonts ${classes.label}`}>
                        Payment Mode
                      </Typography>
                      <TextField
                        select
                        className={`Fonts ${classes.textField}`}
                        variant="outlined"
                        value={form.accountId}
                        onChange={handleChange("accountId")}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {accounts?.map((a) => (
                          <MenuItem
                            className="Fonts"
                            key={a?.accountId}
                            value={a?.accountId}
                          >
                            {a?.bankName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography className={`Fonts ${classes.label}`}>
                        Tenure
                        <span className={classes.required}>*</span>
                      </Typography>

                      <TextField
                        select
                        variant="standard"
                        value={form.tenureType}
                        onChange={handleChange("tenureType")}
                        // InputProps={{
                        //   disableUnderline: true,
                        // }}
                        className={classes.tenureType}
                      >
                        <MenuItem value="daily" className="Fonts">
                          Daily
                        </MenuItem>
                        <MenuItem value="weekly" className="Fonts">
                          Weekly
                        </MenuItem>
                        <MenuItem value="ten_daily" className="Fonts">
                          Ten-Daily
                        </MenuItem>
                        <MenuItem value="bi_weekly" className="Fonts">
                          Bi-Weekly
                        </MenuItem>
                        <MenuItem value="monthly" className="Fonts">
                          Monthly
                        </MenuItem>
                      </TextField>
                    </Box>

                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder=""
                      value={form.tenure}
                      onChange={handleChange("tenure")}
                      className={`Fonts ${classes.textField}`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Collection Amount
                      <span className={classes.required}>*</span>
                    </Typography>
                    <TextField
                      type="number"
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      InputProps={{ startAdornment: "INR " }}
                      value={form.dailyCollAmt}
                      error={submitted && !form.dailyCollAmt}
                      onChange={handleChange("dailyCollAmt")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={isFixedDeposit ? 12 : 6}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Rate of Interest
                    </Typography>
                    <TextField
                      type="number"
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      inputProps={{ step: "0.01" }}
                      InputProps={{ endAdornment: "%" }}
                      value={form.rateOfInterest}
                      onChange={handleChange("rateOfInterest")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={isFixedDeposit ? 12 : 6}>
                    <Typography className={`Fonts ${classes.label}`}>
                      Processing Fee
                    </Typography>
                    <TextField
                      type="number"
                      className={`Fonts ${classes.textField}`}
                      variant="outlined"
                      InputProps={{ startAdornment: "INR " }}
                      value={form.processFee}
                      onChange={handleChange("processFee")}
                    />
                  </Grid>

                  {!isFixedDeposit && (
                    <Grid item xs={12}>
                      <Typography className={`Fonts ${classes.label}`}>
                        Payment Mode
                      </Typography>
                      <TextField
                        select
                        className={`Fonts ${classes.textField}`}
                        variant="outlined"
                        value={form.accountId}
                        onChange={handleChange("accountId")}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {accounts?.map((a) => (
                          <MenuItem
                            className="Fonts"
                            key={a?.accountId}
                            value={a?.accountId}
                          >
                            {a?.bankName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.calcButtonsRow}>
                  <Button
                    className={`${classes.calculateButton} Fonts`}
                    onClick={handleCalculate}
                  >
                    Calculate
                  </Button>
                  <Button
                    className={`${classes.resetButton} Fonts`}
                    variant="outlined"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </div>
              </Grid>

              {schedule.length > 0 && (
                <Grid item xs={12} md={4} style={{ marginLeft: "auto" }}>
                  <div className={classes.totalWrapper}>
                    <div className={`Fonts ${classes.totalCircle}`}>
                      {maturityValue}
                    </div>
                    <Typography className={`Fonts ${classes.totalLabel}`}>
                      Maturity Value
                    </Typography>
                  </div>
                </Grid>
              )}
            </Grid>
          )}
        </Card>

        {schedule.length > 0 && (
          <div className={classes.scheduleSection}>
            <Typography className={`Fonts ${classes.scheduleTitle}`}>
              Savings Collection Schedule
            </Typography>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell className="Fonts">EMI No.</TableCell>
                  <TableCell className="Fonts">Due Date</TableCell>
                  <TableCell className="Fonts">Opening Balance</TableCell>
                  <TableCell className="Fonts">Collection Amount</TableCell>
                  <TableCell className="Fonts">Closing Balance</TableCell>
                  {!isFixedDeposit && (
                    <TableCell className="Fonts">
                      Compounding Interest
                    </TableCell>
                  )}
                  {!isFixedDeposit && (
                    <TableCell className="Fonts">Interest</TableCell>
                  )}
                  {!isFixedDeposit && (
                    <TableCell className="Fonts">Maturity Amount</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {schedule.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{formatDate(row.dueDate)}</TableCell>
                    <TableCell>
                      {Number(row.openingBalance || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {Number(row.collectionAmount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {Number(row.closingBalance || 0).toFixed(2)}
                    </TableCell>
                    {!isFixedDeposit && (
                      <TableCell>
                        {Number(row.compoundInterest || 0).toFixed(2)}
                      </TableCell>
                    )}
                    {!isFixedDeposit && (
                      <TableCell>
                        {Number(row.interest || 0).toFixed(2)}
                      </TableCell>
                    )}
                    {!isFixedDeposit && (
                      <TableCell>
                        {Number(row.maturityAmount || 0).toFixed(2)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className={classes.rowCenter}>
          <div className={classes.assignRow}>
            <Typography
              component={"h6"}
              className={`Fonts ${classes.label}`}
              style={{
                minWidth: 180,
                fontWeight: 600,
                fontSize: 18,
                color: "#3E4664",
              }}
            >
              Assign to Collection Agent
            </Typography>
            <TextField
              select
              variant="outlined"
              style={{ minWidth: 240 }}
              value={form.collectorId}
              onChange={handleChange("collectorId")}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {collectors.map((c) => (
                <MenuItem key={c.userId} value={c.userId}>
                  {c.firstName + " " + c?.lastName}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className={classes.actionButtons}>
            <Button
              className={`Fonts ${classes.cancelButton}`}
              onClick={backLinkAction}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className={`Fonts ${classes.saveButton}`}
              onClick={handleSave}
              disabled={saving || loading || schemeSaving}
            >
              {saving ? (
                <CircularProgress size={18} style={{ color: "#fff" }} />
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSaving;
